// SKILL-PROPOSALS-OPERATOR in the in-memory API: the operator console's Skill proposals
// (`operator.skillProposals.list|get|decide|ship`), with the real routes' rules — platform super
// admins only (404 to anyone else; the twin's super admin is an agency `admin`), granted
// companies only, newest first, a decision only while proposed or ready (a repeat returns the
// first; the other one is a 409), shipping only once approved. The demo companies start with no
// proposal; `fileProposal` (the `__companyosFakeSkillProposal` hook) files one as a company's DE
// would, optionally already reviewed by Skillsmith.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Ctx, type Handler, json, notFound, problem, validation } from "./fake";
import { type FakeCompany, uid } from "./fake-data";

type Proposal = OperationOutput<"operator.skillProposals.get">;

export type FakeSkillProposal = Pick<Proposal, "name" | "why"> &
  Partial<Pick<Proposal, "draft" | "evidence" | "review" | "source" | "outcome">> & {
    proposer?: string;
  };

export function createOperatorSkillProposalsFake(deps: {
  now: () => Date;
  companies: () => FakeCompany[];
}) {
  let seq = 0;
  const rows: Proposal[] = [];

  function grantedOf(ctx: Ctx): Set<string> | null {
    const agency = ctx.me.agency;
    if (agency?.role !== "admin") return null;
    return new Set(agency.grants.map((g) => g.company_id));
  }

  function fileProposal(companyId: string, p: FakeSkillProposal): Proposal {
    const c = deps.companies().find((x) => x.id === companyId);
    if (!c) throw new Error(`no company ${companyId}`);
    const row: Proposal = {
      company: { id: c.id, slug: c.slug, name: c.name },
      id: uid(0x5c10, ++seq),
      name: p.name,
      why: p.why,
      evidence: p.evidence ?? [{ kind: "run", id: uid(0x5c11, seq) }],
      draft: p.draft ?? `---\nname: ${p.name}\n---\n# ${p.name}\n`,
      status: p.review ? "ready" : "proposed",
      proposer: { membership_id: uid(0x5c12, seq), name: p.proposer ?? "DE · Assistant" },
      run_id: null,
      review: p.review ?? null,
      decided_at: null,
      decision_note: null,
      work_item_id: null,
      shipped_at: null,
      created_at: deps.now().toISOString(),
      source: p.source ?? "de",
      outcome: p.outcome ?? null,
      expected_effect: null,
      consent: {
        state: "needs_client_ok",
        reason: null,
        set_at: null,
        message_draft: null,
        message_sent_at: null,
        blocks_shipping: true,
      },
    };
    rows.push(row);
    return row;
  }

  /** The list view: the drafts left out. */
  const listView = (p: Proposal): Proposal => ({
    ...p,
    draft: null,
    review: p.review ? { ...p.review, draft: null } : null,
    consent: { ...p.consent, message_draft: null },
  });

  const settled = (p: Proposal) =>
    p.consent.state === "client_ok" || p.consent.state === "no_consent_needed";

  function find(ctx: Ctx): Proposal | null {
    const granted = grantedOf(ctx);
    if (!granted) return null;
    const p = rows.find(
      (r) => r.company.id === ctx.params.company_id && r.id === ctx.params.proposal_id,
    );
    return p && granted.has(p.company.id) ? p : null;
  }

  const handlers: Partial<Record<RouteId, Handler>> = {
    "operator.skillProposals.list": (ctx) => {
      const granted = grantedOf(ctx);
      if (!granted) return notFound();
      const status = ctx.url.searchParams.get("status");
      const data = rows
        .filter((r) => granted.has(r.company.id) && (!status || r.status === status))
        .sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id.localeCompare(a.id))
        .map(listView);
      return json({ data });
    },

    "operator.skillProposals.get": (ctx) => {
      const p = find(ctx);
      return p ? json(p) : notFound();
    },

    "operator.skillProposals.decide": (ctx) => {
      const p = find(ctx);
      if (!p) return notFound();
      const b = (ctx.body ?? {}) as { decision?: unknown; note?: unknown };
      if (b.decision !== "approve" && b.decision !== "reject")
        return validation("decision must be approve or reject");
      const want = b.decision === "approve" ? "approved" : "rejected";
      if (p.status !== "proposed" && p.status !== "ready") {
        if (p.status === want || (want === "approved" && p.status === "shipped")) return json(p);
        return problem(409, "conflict", "Conflict", `the proposal is already ${p.status}`);
      }
      p.status = want;
      p.decided_at = deps.now().toISOString();
      p.decision_note = typeof b.note === "string" && b.note.trim() ? b.note.trim() : null;
      if (want === "approved") p.work_item_id = uid(0x5c13, ++seq);
      return json(p);
    },

    "operator.skillProposals.ship": (ctx) => {
      const p = find(ctx);
      if (!p) return notFound();
      if (p.status === "shipped") return json(p);
      if (p.status !== "approved")
        return problem(409, "conflict", "Conflict", `a ${p.status} proposal cannot ship`);
      // NIGHTLY-REVIEW: the client conversation first.
      if (!settled(p))
        return problem(
          409,
          "conflict",
          "Conflict",
          "the client conversation is not settled: record the client's OK, or why no consent is needed, before shipping",
        );
      p.status = "shipped";
      p.shipped_at = deps.now().toISOString();
      return json(p);
    },

    // NIGHTLY-REVIEW: the client conversation.
    "operator.skillProposals.consent": (ctx) => {
      const p = find(ctx);
      if (!p) return notFound();
      const b = (ctx.body ?? {}) as { state?: unknown; reason?: unknown };
      if (
        b.state !== "needs_client_ok" &&
        b.state !== "client_ok" &&
        b.state !== "no_consent_needed"
      )
        return validation("state must be needs_client_ok, client_ok or no_consent_needed");
      if (p.status === "rejected" || p.status === "shipped")
        return problem(
          409,
          "conflict",
          "Conflict",
          `a ${p.status} proposal's client conversation is closed`,
        );
      const reason = typeof b.reason === "string" && b.reason.trim() ? b.reason.trim() : null;
      if (b.state === "no_consent_needed" && !reason)
        return validation("say why no client consent is needed");
      p.consent = {
        ...p.consent,
        state: b.state,
        reason,
        set_at: deps.now().toISOString(),
        blocks_shipping: b.state === "needs_client_ok",
      };
      return json(p);
    },

    "operator.skillProposals.clientMessage.draft": (ctx) => {
      const p = find(ctx);
      if (!p) return notFound();
      if (p.status === "rejected" || p.status === "shipped")
        return problem(
          409,
          "conflict",
          "Conflict",
          `a ${p.status} proposal has nothing to tell the client`,
        );
      const change =
        p.outcome ?? `${p.proposer.name} will handle a part of its work a little differently`;
      const body =
        `Hello,\n\nWe review how your digital employees work and would like to make a small change at ${p.company.name}:\n\n` +
        `${change}\n\nNothing changes until you say so. If this works for you, reply with a quick yes.\n\nSupport`;
      p.consent = { ...p.consent, message_draft: body };
      return json({
        subject: `A small change to how ${p.proposer.name} works`,
        body,
        to: [{ membership_id: uid(0x5c14, 1), name: "Owner", email: "owner@example.test" }],
        edit_url: `/operator/skill-proposals/${p.company.id}/${p.id}`,
      });
    },

    "operator.skillProposals.clientMessage.send": (ctx) => {
      const p = find(ctx);
      if (!p) return notFound();
      const b = (ctx.body ?? {}) as { subject?: unknown; body?: unknown; to?: unknown };
      if (typeof b.subject !== "string" || typeof b.body !== "string" || !Array.isArray(b.to))
        return validation("subject, body and to are required");
      if (p.status === "rejected" || p.status === "shipped")
        return problem(
          409,
          "conflict",
          "Conflict",
          `a ${p.status} proposal has nothing to tell the client`,
        );
      p.consent = {
        ...p.consent,
        message_draft: `${b.subject}\n\n${b.body}`,
        message_sent_at: deps.now().toISOString(),
      };
      return json(p);
    },
  };

  return { handlers, fileProposal };
}
